import type { AssessmentResult } from '../types.ts';

export type AssessErrorCode =
  | 'no-speech' // nic nie usłyszano — dziecko może spróbować ponownie (nie liczymy podejścia)
  | 'mic-denied' // brak zgody na mikrofon
  | 'no-mic' // przeglądarka nie uruchomiła mikrofonu (brak urządzenia, zajęty, zablokowany w systemie)
  | 'network' // problem z siecią / usługą
  | 'token' // nie udało się pobrać tokenu Azure
  | 'unpaired' // serwer odrzucił urządzenie (brak klucza, zły klucz albo urządzenie wyłączone przez rodzica) — trzeba sparować
  | 'limit' // dzienny limit tokenów Azure dla tego urządzenia jest wyczerpany
  | 'sdk' // nie udało się załadować SDK
  | 'unsupported' // ta przeglądarka nie obsługuje tej metody
  | 'unavailable'; // żadna metoda oceny nie zadziałała

export class AssessError extends Error {
  code: AssessErrorCode;
  /** Dla 'unavailable': kody błędów poszczególnych metod (do wyboru czytelnego komunikatu). */
  causes: AssessErrorCode[] = [];
  constructor(code: AssessErrorCode, message?: string) {
    super(message ?? code);
    this.name = 'AssessError';
    this.code = code;
  }
}

export interface AssessHooks {
  /**
   * Mikrofon i rozpoznawanie są gotowe — dopiero teraz wolno zacząć mówić. Wcześniej pierwsze słowo ginie (mikrofon otwiera się i łączy z usługą
   * przez ułamek sekundy po kliknięciu), a krótkie „I” na początku zdania znika najłatwiej. Może być wywołane więcej niż raz (metoda zapasowa).
   */
  onReady?: () => void;
  /**
   * Nagranie wypowiedzi (16 kHz, 16 bit, mono, przycięte do mowy) po UDANEJ ocenie — raz, zanim mikrofon zostanie zwolniony. Po błędzie (cisza, brak sieci) nie jest wołane,
   * bo wtedy nie ma próby, do której dałoby się je dopiąć. Służy do zapisu do strojenia oceny (SPEC §23.39); wyjątek z tego wywołania nie psuje oceny.
   */
  onRecording?: (pcm: Int16Array) => void;
  /**
   * Surowe odpowiedzi Azure dla tej próby (JSON dokładnie tak, jak je zwrócił SDK): ocena wymowy ze zdaniem wzorcowym i zwykłe rozpoznawanie bez wzorca — po UDANEJ ocenie, razem
   * z `onRecording`. Służą do zapisu do strojenia (SPEC §23.40): z nich da się przeliczyć werdykt innymi progami bez nowych nagrań. Wyjątek z tego wywołania nie psuje oceny.
   */
  onAzureRaw?: (raw: AzureRawPair) => void;
}

/** Surowe JSON-y z Azure dla jednej próby (brakujące pole = ta odpowiedź nie była dostępna). */
export interface AzureRawPair {
  scripted?: string;
  plain?: string;
  /**
   * Ile razy próbę powtórzono na zapisanym nagraniu (zawieszone połączenie, SPEC §23.40). Odpowiedź z powtórki ma czasy liczone od początku NAGRANIA, a nie od otwarcia mikrofonu,
   * więc przy `replays > 0` nie wiadomo, od czego liczą się czasy słów — kalibracja nie rysuje wtedy granic słów na spektrogramie.
   */
  replays?: number;
}

export interface Assessor {
  name: 'azure' | 'browser';
  /** Nasłuchuje mikrofonu i ocenia wypowiedź względem zdania wzorcowego. */
  assess(reference: string, hooks?: AssessHooks): Promise<AssessmentResult>;
}
